import {
  AdornedInput,
  Box,
  Combobox,
  type ComboboxOptionType,
  DropdownSelect,
  Icon,
  Tooltip,
} from '@zillow/constellation-10';
import { IconClockFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';

type Props = {
  onTimePickerChange(tourScheduledTimestampMs: string): void;
  tourScheduledTimestampMs: string | undefined | null;
};

const TIME_PICKER_FORMAT = 'h:mm aaa';
const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_OPTION = 15;

const { getTimestampMsFromTimeValues, getTimeValuesFromTimestampMs } = dateTimeUtils;

const formatMinutesOfDay = (minutesOfDay: number): string => {
  const hours = Math.floor(minutesOfDay / 60);
  const minutes = minutesOfDay % 60;

  return `${hours % 12 || 12}:${`${minutes}`.padStart(2, '0')} ${hours < 12 ? 'am' : 'pm'}`;
};

const TIME_OPTIONS = Array.from({ length: MINUTES_PER_DAY / MINUTES_PER_OPTION }, (_, index) =>
  formatMinutesOfDay(index * MINUTES_PER_OPTION),
);

const TimePicker = ({ onTimePickerChange, tourScheduledTimestampMs }: Props): JSX.Element => {
  const { isImpersonator } = useContext(AppContext);
  const [currentTimestampMs, setCurrentTimestampMs] = useState(tourScheduledTimestampMs);

  useEffect(() => {
    setCurrentTimestampMs(tourScheduledTimestampMs);
  }, [tourScheduledTimestampMs]);

  const selectedTime = currentTimestampMs ? format(+currentTimestampMs, TIME_PICKER_FORMAT) : '';

  // A tour scheduled from the date picker inherits the current wall-clock minute, so the selected
  // time regularly falls between the 15-minute options and has to be added to the list — the input
  // renders blank for a value the listbox doesn't contain.
  const timeOptions = useMemo(() => {
    if (!currentTimestampMs || TIME_OPTIONS.includes(selectedTime)) {
      return TIME_OPTIONS;
    }

    const selectedDate = new Date(+currentTimestampMs);
    const insertionIndex = Math.ceil((selectedDate.getHours() * 60 + selectedDate.getMinutes()) / MINUTES_PER_OPTION);

    return [...TIME_OPTIONS.slice(0, insertionIndex), selectedTime, ...TIME_OPTIONS.slice(insertionIndex)];
  }, [currentTimestampMs, selectedTime]);

  const handleTimeSelect = (option: ComboboxOptionType): void => {
    if (typeof option !== 'string') {
      return;
    }

    const newTimestampMs = getTimestampMsFromTimeValues({
      newTimeValues: getTimeValuesFromTimestampMs(option),
      currentTimestampMs,
    });

    setCurrentTimestampMs(newTimestampMs);
    onTimePickerChange(newTimestampMs);
  };

  if (!tourScheduledTimestampMs || isImpersonator) {
    return <TooltipContent isImpersonator={isImpersonator} />;
  }

  return (
    <DropdownSelect
      options={timeOptions}
      value={selectedTime}
      onOptionSelect={handleTimeSelect}
      renderAdornment={(adornmentProps) => {
        const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;

        return (
          <Combobox.Adornment {...adornmentPropsWithoutRef}>
            {/* ! outranks Combobox.Adornment's descendant `.c11n_icon` color rule */}
            <Icon size="md" css={{ color: 'icon.neutral!' }} render={<IconClockFilled />} />
          </Combobox.Adornment>
        );
      }}
    />
  );
};

const TooltipContent = ({ isImpersonator }: { isImpersonator: boolean }): JSX.Element => {
  const tooltipText = isImpersonator ? StringConstants.TOOLTIP.IMPERSONATION : 'Please provide a date first.';

  return (
    // Keeps the trigger from being a direct FormField child, which FormField styles as its label tooltip.
    <Box>
      <Tooltip
        content={tooltipText}
        trigger={
          <AdornedInput
            disabled={true}
            input={<AdornedInput.Input placeholder="00:00 am" />}
            endAdornment={
              <AdornedInput.Adornment>
                <AdornedInput.Icon>
                  <IconClockFilled />
                </AdornedInput.Icon>
              </AdornedInput.Adornment>
            }
          />
        }
      />
    </Box>
  );
};

export default TimePicker;
